import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface Project {
  id: string;
  name: string;
  active: boolean;
}

interface PropertyType {
  id: string;
  name: string;
  active: boolean;
}

interface PropertyCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PropertyCreateDialog: React.FC<PropertyCreateDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    code: '',
    floor: 1,
    contact: '',
    projectId: '',
    propertyTypeId: ''
  });

  const [projects, setProjects] = useState<Project[]>([]);
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();

  // Load initial data when dialog opens
  useEffect(() => {
    if (open) {
      fetchInitialData();
      resetForm();
    }
  }, [open]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [projectsRes, propertyTypesRes] = await Promise.all([
        DataService.get('api/Project/all'),
        DataService.get('api/property-types/all')
      ]);

      // Process projects
      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        let projectList: Project[] = [];
        if (projectsData.success && projectsData.data) {
          projectList = projectsData.data.filter((p: Project) => p.active);
        } else if (Array.isArray(projectsData)) {
          projectList = projectsData.filter((p: Project) => p.active);
        }
        setProjects(projectList);
      }

      // Process property types
      if (propertyTypesRes.ok) {
        const propertyTypesData = await propertyTypesRes.json();
        let propertyTypeList: PropertyType[] = [];
        if (propertyTypesData.success && propertyTypesData.data) {
          propertyTypeList = propertyTypesData.data.filter((pt: PropertyType) => pt.active);
        } else if (Array.isArray(propertyTypesData)) {
          propertyTypeList = propertyTypesData.filter((pt: PropertyType) => pt.active);
        }
        setPropertyTypes(propertyTypeList);
      }

    } catch (error) {
      console.error('Error fetching initial data:', error);
      dispatch(showAlert({
        message: 'Failed to load form data',
        type: 'error'
      }));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      floor: 1,
      contact: '',
      projectId: '',
      propertyTypeId: ''
    });
    setErrors({});
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Property validation
    if (!formData.code.trim()) {
      newErrors.code = 'Property code is required';
    }

    if (formData.floor < 0) {
      newErrors.floor = 'Floor must be a positive number';
    }

    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }

    if (!formData.propertyTypeId) {
      newErrors.propertyTypeId = 'Property type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const payload = {
        code: formData.code,
        floor: formData.floor,
        contact: formData.contact,
        projectId: formData.projectId,
        propertyTypeId: formData.propertyTypeId,
        units: []
      };

      console.log('Creating property with data:', payload);

      const response = await DataService.post('api/Property/create', payload);

      if (response.ok) {
        dispatch(showAlert({
          message: 'Property created successfully!',
          type: 'success'
        }));
        onSuccess();
      } else {
        let errorMessage = 'Failed to create property';
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          errorMessage = `Failed to create property: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating property:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create property. Please try again.';
      dispatch(showAlert({
        message: errorMessage,
        type: 'error'
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Create New Property
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add a new property to your portfolio
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading form data...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Property Information */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon color="primary" />
                  Property Information
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Property Code"
                      value={formData.code}
                      onChange={(e) => handleInputChange('code', e.target.value)}
                      error={!!errors.code}
                      helperText={errors.code}
                      disabled={submitting}
                      required
                      placeholder="e.g., p01"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Floor"
                      type="number"
                      value={formData.floor}
                      onChange={(e) => handleInputChange('floor', parseInt(e.target.value) || 0)}
                      error={!!errors.floor}
                      helperText={errors.floor}
                      disabled={submitting}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      value={formData.contact}
                      onChange={(e) => handleInputChange('contact', e.target.value)}
                      error={!!errors.contact}
                      helperText={errors.contact}
                      disabled={submitting}
                      placeholder="e.g., abo issam"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.projectId} required>
                      <InputLabel>Project</InputLabel>
                      <Select
                        value={formData.projectId}
                        label="Project"
                        onChange={(e) => handleInputChange('projectId', e.target.value)}
                        disabled={submitting}
                      >
                        {projects.map((project) => (
                          <MenuItem key={project.id} value={project.id}>
                            {project.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.projectId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.projectId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.propertyTypeId} required>
                      <InputLabel>Property Type</InputLabel>
                      <Select
                        value={formData.propertyTypeId}
                        label="Property Type"
                        onChange={(e) => handleInputChange('propertyTypeId', e.target.value)}
                        disabled={submitting}
                      >
                        {propertyTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.propertyTypeId && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.propertyTypeId}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
          size="large"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting || loading}
          variant="contained"
          size="large"
          startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
        >
          {submitting ? 'Creating...' : 'Create Property'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PropertyCreateDialog;